import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { doOpenCall } from '../../_utilities/doOpenCall'
import type { CalendarBooking } from '../../_types/calendar-booking'
import type { CalendarEventType } from '../../_types/calendar-event-type'
import type { AvailabilitySlot } from '../../_types/availability'
import SlotPicker from '../../components/slot-picker/SlotPicker'
import BookingConfirmation from '../../components/booking-confirmation/BookingConfirmation'
import {
  formatLongDateInTimezone,
  formatTimeInTimezone,
} from '../../_utilities/formatDateTime'
import { getLocationLabel } from '../booking-page/helpers'
import { detectBrowserTimezone } from '../../_data/timezones'
import {
  Card,
  Centered,
  ErrorState,
  ExistingBookingLabel,
  ExistingBookingPanel,
  ExistingBookingValue,
  Heading,
  PageShell,
} from './styled'

interface BookingWithEventType {
  booking: CalendarBooking
  eventType: CalendarEventType
}

type ReschedulePageStep = 'picking' | 'confirmed'

const ReschedulePage = (): JSX.Element => {
  const parameters = useParams<{ token: string }>()
  const [data, setData] = useState<BookingWithEventType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [step, setStep] = useState<ReschedulePageStep>('picking')
  const [isRescheduling, setIsRescheduling] = useState<boolean>(false)
  const [rescheduleError, setRescheduleError] = useState<string>('')
  const [confirmedStart, setConfirmedStart] = useState<number>(0)
  const [confirmedEnd, setConfirmedEnd] = useState<number>(0)

  const browserTimezone = useMemo(() => detectBrowserTimezone(), [])
  const [viewerTimezone, setViewerTimezone] = useState<string>(browserTimezone)

  const loadBooking = useCallback(async (): Promise<void> => {
    if (!parameters.token) {
      setErrorMessage('Reschedule link is missing a token.')
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    try {
      const response = await doOpenCall('publiccalendar-getbookingbytoken', {
        token: parameters.token,
        kind: 'reschedule',
      })
      if (response.code === 200) {
        setData(JSON.parse(response.data) as BookingWithEventType)
        setErrorMessage('')
      } else {
        setErrorMessage(response.message || 'Booking not found.')
      }
    } catch {
      setErrorMessage('Could not reach the booking service. Try again shortly.')
    } finally {
      setIsLoading(false)
    }
  }, [parameters.token])

  useEffect(() => {
    loadBooking()
  }, [loadBooking])

  const handleSelectNewSlot = useCallback(
    async (slot: AvailabilitySlot): Promise<void> => {
      if (!data) {
        return
      }
      setIsRescheduling(true)
      setRescheduleError('')
      try {
        const response = await doOpenCall('publiccalendar-reschedulebooking', {
          rescheduleToken: parameters.token,
          newStartTime: slot.startTime,
          rescheduledBy: 'guest',
        })
        if (response.code === 200) {
          setConfirmedStart(slot.startTime)
          setConfirmedEnd(slot.endTime)
          setStep('confirmed')
        } else {
          setRescheduleError(response.message || 'Failed to reschedule.')
        }
      } catch {
        setRescheduleError(
          'Could not reach the booking service. Try again shortly.',
        )
      } finally {
        setIsRescheduling(false)
      }
    },
    [data, parameters.token],
  )

  if (isLoading) {
    return (
      <PageShell>
        <Centered>Loading your booking…</Centered>
      </PageShell>
    )
  }

  if (errorMessage.length > 0 || !data) {
    return (
      <PageShell>
        <ErrorState>{errorMessage || 'Booking not found.'}</ErrorState>
      </PageShell>
    )
  }

  if (step === 'confirmed') {
    return (
      <PageShell>
        <Card>
          <BookingConfirmation
            headline="Your booking has been moved."
            subline="A confirmation email is on its way to both of you."
            bookingId={data.booking.id}
            guestName={data.booking.guestName}
            guestEmail={data.booking.guestEmail}
            startTime={confirmedStart}
            endTime={confirmedEnd}
            timezone={viewerTimezone}
            hostTimezone={data.eventType.timezone || browserTimezone}
            eventTypeTitle={data.eventType.title}
            locationLabel={getLocationLabel(data.eventType)}
            descriptionHtml={data.eventType.description}
            videoRoomUrl={data.booking.videoRoomUrl ?? ''}
            rescheduleToken={data.booking.rescheduleToken}
            cancelToken={data.booking.cancelToken}
          />
        </Card>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Card>
        <Heading>Reschedule your booking</Heading>
        <ExistingBookingPanel>
          <ExistingBookingLabel>Currently booked for</ExistingBookingLabel>
          <ExistingBookingValue>
            {formatLongDateInTimezone(
              data.booking.startTime,
              data.eventType.timezone || browserTimezone,
            )}
            {' • '}
            {formatTimeInTimezone(
              data.booking.startTime,
              data.eventType.timezone || browserTimezone,
            )}
            {' – '}
            {formatTimeInTimezone(
              data.booking.endTime,
              data.eventType.timezone || browserTimezone,
            )}
          </ExistingBookingValue>
        </ExistingBookingPanel>
        <SlotPicker
          agentUserId={data.booking.agentUserId}
          eventTypeId={data.booking.eventTypeId}
          durationOptionsMinutes={[data.booking.durationMinutes]}
          defaultDurationMinutes={data.booking.durationMinutes}
          hostTimezone={data.eventType.timezone || browserTimezone}
          viewerTimezone={viewerTimezone}
          onChangeViewerTimezone={setViewerTimezone}
          selectedSlot={null}
          onSelectSlot={handleSelectNewSlot}
        />
        {isRescheduling ? <Centered>Saving your new time…</Centered> : null}
        {rescheduleError.length > 0 ? (
          <ErrorState>{rescheduleError}</ErrorState>
        ) : null}
      </Card>
    </PageShell>
  )
}

export default ReschedulePage
